<template>
  <div>
    <CollectionDetails
      :isOpen.sync="collectionDetailsPanelOpen"
      :selectedCharges="collectionCharges"
      :selectedDates="selectedCalendarDates"
      @updateCharges="loadCharges()"
      @updateCollections="loadCollection()"
    />

    <v-flex xs12>
      <div class="elevation-1 text-center paper mb-1">
        <v-tabs v-model="tab">
          <v-tabs-slider></v-tabs-slider>

          <v-tab class="tab">
            {{ $t('income.collection.title') }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab" class="tabs-items">
          <v-tab-item>
            <div class="d-flex flex-column py-4">
              <div>
                <div class="d-flex justify-end mb-2">
                  <div class="d-flex align-row">
                    <v-btn
                      fab
                      x-small
                      depressed
                      :plain="true"
                      :ripple="false"
                      color="#0F6FFF"
                      @click="loadCollection()"
                      class="mr-1">
                      <v-icon>$refresh</v-icon>
                    </v-btn>
                    <div class="refreshButtonText my-auto">
                      <a @click="loadCollection()">{{ $t('income.collection.buttonUpdate') }}</a>
                    </div>
                  </div>
                </div>
                <div class="d-flex justify-space-between">
                  <div class="d-flex">
                    <div class="mr-4">
                      <Select
                        size="x-large"
                        item-text="name"
                        item-value="_id"
                        :items="banks.data"
                        :disabled="isEmpty(banks.data)"
                        v-model.lazy="bank"
                        :label="$t('income.collection.collectionAccountDialog.bank')"
                        @change="onChangeBank()"
                        hide-details
                        required
                      />
                    </div>
                    <div>
                      <Select
                        size="x-large"
                        item-text="name"
                        item-value="id"
                        :items="accounts.data"
                        :disabled="isEmpty(accounts.data)"
                        v-model.lazy="account"
                        :label="$t('income.collection.collectionAccountDialog.account')"
                        @change="loadCollection()"
                        hide-details
                        required
                      />
                    </div>
                  </div>
                  <div class="d-flex align-center">
                    <v-dialog
                      @keydown.esc="dateModal=false"
                      ref="dialog"
                      v-model="dateModal"
                      :return-value.sync="selectedCalendarDates"
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="dateRangeText"
                          :label="$t('income.collection.collectionDateDialog.date')"
                          readonly
                          outlined
                          dense
                          v-bind="attrs"
                          v-on="on"
                          class="calendarTextField"
                        >
                          <v-icon slot="append" color="#01D28E">
                            mdi-calendar
                        </v-icon>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="selectedCalendarDates"
                        scrollable
                        ref="picker"
                        color="primary"
                        range
                        :first-day-of-week="1"
                        :allowed-dates="allowedDates"
                        @input="orderDateRange"
                        reactive
                        no-title
                        class="pt-4"
                        :locale="locale">
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="dateModal = false"
                          class="dateButton"
                        >
                          {{ $t('income.collection.dateModal.cancel') }}
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="confirmCalendarDates"
                          class="dateButton"
                        >
                          {{ $t('income.collection.dateModal.ok') }}
                        </v-btn>
                      </v-date-picker>
                    </v-dialog>
                  </div>
                </div>
                <div v-if="!isEmpty(tableCollection.data)">
                  <v-data-table
                    :loading-text="tableCollection.loadingText"
                    :loading="tableCollection.loading"
                    :headers="tableCollection.headers"
                    :items="tableCollection.data"
                    :options="tableCollection.options"
                    :server-items-length="tableCollection.total"
                    :footer-props="{
                      'items-per-page-options': [10, 20, 30, 40, 50]
                    }"
                    @update:options="updateTable"
                    class="tableCollection">
                    <template v-slot:[`item.concept`]="{ item }">
                      <div>{{ item.concept }}</div>
                    </template>
                    <template v-slot:[`item.date`]="{ item }">
                      <div>{{ $moment(item.date).format('DD/MM/YYYY') }}</div>
                    </template>
                    <template v-slot:[`item.amount`]="{ item }">
                      <div class="text-right">{{ item.amount | toFix2 | numberWithComma | prefix('$') }} {{ item.currency }}</div>
                    </template>
                    <template v-slot:[`item.applied`]="{ item }">
                      <div :class="item.applied ? 'blueText' : 'redText'">
                        {{ item.applied ? $t('income.collection.tableCollection.appliedTypes.applied') : $t('income.collection.tableCollection.appliedTypes.notApplied') }}
                      </div>
                    </template>
                    <template v-slot:[`item.incomeType`]="{ item }">
                      <div>
                        <Select
                        v-if="expensesPermissions['update-income-type']"
                          size="fullWidth"
                          item-text="name"
                          item-value="_id"
                          :items="incomeTypes.data"
                          :disabled="incomeTypes.loading"
                          v-model.lazy="item.incomeType"
                          @change="relateIncomeType(item.incomeType, item._id)"
                          class="pr-1"
                        />
                      </div>
                    </template>
                    <template v-slot:[`item.incomeCategory`]="{ item }">
                      <div v-if="item.incomeType && !checkIfIncludedInCharges(item.incomeType)">
                        <Select
                          v-if="expensesPermissions['update-income-category']"
                          size="fullWidth"
                          item-text="name"
                          item-value="_id"
                          :items="filterCategoriesByType(item.incomeType)"
                          :disabled="incomeCategories.loading"
                          v-model.lazy="item.incomeCategory"
                          @change="relateIncomeCategory(item.incomeCategory, item._id)"
                          class="pr-1"
                        />
                      </div>
                    </template>
                    <template v-slot:[`item.paymentType`]="{ item }">
                      <div v-if="checkIfIncludedInCharges(item.incomeType)">
                        <Select
                        v-if="paymentMethodsPermissions['update-payment-method']"
                          size="fullWidth"
                          item-text="name"
                          item-value="_id"
                          :items="paymentMethods.data"
                          :disabled="paymentMethods.loading"
                          v-model.lazy="item.incomePaymentMethod"
                          @change="relatePaymentType(item.incomePaymentMethod, item._id)"
                          class="pr-1"
                        />
                      </div>
                    </template>
                  </v-data-table>
                </div>
                <div v-else>
                  <v-icon class="bankDataIcon">$bankData</v-icon>
                  <p class="noDataText">{{ $t('income.collection.noData') }}</p>
                </div>
              </div>
            </div>
            <v-dialog @keydown.esc="collectionAccountDialogOpen=false" v-model="collectionAccountDialogOpen" persistent max-width="453px">
              <v-card>
                <div class="d-flex justify-end">
                  <v-btn
                    fab
                    small
                    color="transparent"
                    depressed
                    @click="closeCollectionAccountDialog"
                    class="mr-1 mt-1"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
                <v-card-title class="dialogTitle">{{ $t('income.collection.collectionAccountDialog.title') }}</v-card-title>
                <v-card-text class="d-flex flex-column align-center">
                  <div class="mt-4 text-center">
                    <div class="select">
                      <Select
                        size="x-large"
                        item-text="name"
                        item-value="_id"
                        :items="banks.data"
                        :disabled="isEmpty(banks.data)"
                        v-model.lazy="bank"
                        :label="$t('income.collection.collectionAccountDialog.bank')"
                        @change="onChangeBank()"
                        hide-details
                        required
                      />
                    </div>
                    <div class="select">
                      <Select
                        size="x-large"
                        item-text="name"
                        item-value="id"
                        :items="accounts.data"
                        :disabled="isEmpty(accounts.data)"
                        v-model.lazy="account"
                        :label="$t('income.collection.collectionAccountDialog.account')"
                        hide-details
                        required
                      />
                    </div>
                  </div>
                </v-card-text>
                <v-card-actions class="px-6 justify-end">
                  <Button size="small" @click="collectionDialogNext" :disabled="!bank || (hasFinerioAccount && !account)">
                    {{ $t('income.collection.collectionAccountDialog.buttonNext') }}
                  </Button>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog @keydown.esc="collectionDateDialogOpen=false" v-model="collectionDateDialogOpen" persistent max-width="453px">
              <v-card>
                <div class="d-flex justify-end">
                  <v-btn
                    fab
                    small
                    color="transparent"
                    depressed
                    @click="closeCollectionDateDialog"
                    class="mr-1 mt-1"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
                <v-card-title class="dialogTitle">{{ $t('income.collection.collectionDateDialog.title') }}</v-card-title>
                <v-card-text class="d-flex flex-column align-center">
                  <div>{{ $t('income.collection.collectionDateDialog.subtitle') }}</div>
                  <v-text-field
                    v-model="dateRangeText"
                    :label="$t('income.collection.collectionDateDialog.date')"
                    prepend-icon="mdi-calendar"
                    readonly
                  ></v-text-field>
                  <div class="d-flex justify-center">
                    <v-date-picker
                      v-model="selectedCalendarDates"
                      ref="picker"
                      color="primary"
                      range
                      :first-day-of-week="1"
                      :allowed-dates="allowedDates"
                      @input="orderDateRange"
                      reactive
                      no-title
                      :locale="locale">
                    </v-date-picker>
                  </div>
                </v-card-text>
                <v-card-actions class="px-6 justify-space-between">
                  <Button size="small" @click="collectionDialogBack" class="greyButton">
                    {{ $t('income.collection.collectionDateDialog.buttonBack') }}
                  </Button>
                  <Button size="small" @click="submitCollectionDialog" :disabled="selectedCalendarDates.length != 2">
                    {{ $t('income.collection.collectionDateDialog.buttonSend') }}
                  </Button>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </v-flex>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import CollectionDetails from '@/components/sidePanels/CollectionDetails'
import Button from '@/components/elements/Button'
import Select from '@/components/elements/Select'
import moment from 'moment'
import getLocale from '@/lang/locale'
import isEmpty from 'lodash/isEmpty'

export default {
  name: 'Collection',
  components: {
    Button,
    Select,
    CollectionDetails
  },
  data () {
    return {
      overlay: false,
      section: this.$i18n.t('income.section'),
      tab: null,
      search: null,
      tableCollection: {
        loadingText: this.$i18n.t('income.cashClosings.daily.tableCollection.loading'),
        loading: false,
        headers: [
          { text: this.$i18n.t('income.collection.tableCollection.headers.concept'), value: 'description', sortable: false },
          { text: this.$i18n.t('income.collection.tableCollection.headers.date'), value: 'date', sortable: true },
          { text: this.$i18n.t('income.collection.tableCollection.headers.deposits'), value: 'amount', sortable: false },
          { text: this.$i18n.t('income.collection.tableCollection.headers.applied'), value: 'applied', sortable: true },
          { text: this.$i18n.t('income.collection.tableCollection.headers.incomeType'), value: 'incomeType', sortable: false },
          { text: this.$i18n.t('income.collection.tableCollection.headers.incomeCategory'), value: 'incomeCategory', sortable: false },
          { text: this.$i18n.t('income.collection.tableCollection.headers.paymentType'), value: 'paymentType', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 20,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: ['date'],
          sortDesc: []
        }
      },
      collectionAccountDialogOpen: true,
      collectionDateDialogOpen: false,
      banks: {
        data: [],
        loading: false
      },
      bank: null,
      accounts: {
        data: [],
        loading: false
      },
      account: null,
      dateModal: false,
      paymentMethods: {
        data: [],
        loading: false
      },
      collectionDetailsPanelOpen: false,
      selectedCalendarDates: [],
      collectionCharges: {},
      incomeCategories: {
        data: [],
        loading: false
      },
      incomeTypes: {
        data: [],
        loading: false
      }
    }
  },
  mounted () {
    this.loadBanks()
    this.loadPaymentMethods()
    this.loadCategories()
    this.loadIncomeTypes()
  },
  computed: {
    dateRangeText () {
      return this.selectedCalendarDates.join(' ~ ')
    },
    locale () {
      return getLocale(this.$i18n.locale)
    },
    restaurant () {
      return this.$store.state.currentRestaurant.data
    },
    hasFinerioAccount () {
      const selectedBank = this.banks.data.find(item => item._id === this.bank)
      return !!selectedBank.credentialId
    },
    expensesPermissions () {
      return this.$store.state.login.permissions['expenses'].components['income-category'].actions
    },
    paymentMethodsPermissions () {
      return this.$store.state.login.permissions['expenses'].components['payment-methods'].actions
    }
  },
  watch: {
    '$store.state.currentRestaurant.data': async function (val) {
      this.resetForm()
      await this.loadBanks()
      await this.loadCategories()
    }
  },
  methods: {
    resetForm () {
      this.tableCollection.data = []
      this.tableCollection.total = 0
      this.collectionAccountDialogOpen = true
      this.collectionDateDialogOpen = false
      this.banks.data = []
      this.banks.loading = false
      this.bank = null
      this.accounts.data = []
      this.accounts.loading = false
      this.account = null
      this.dateModal = false
      this.paymentMethods.data = []
      this.paymentMethods.loading = false
      this.selectedCalendarDates = []
      this.collectionCharges = {}
    },
    async updateTable (data) {
      const { sortBy, sortDesc } = data
      const [startDate, endDate] = this.selectedCalendarDates
      this.tableCollection.options.sortDesc = [sortDesc[0]]
      this.tableCollection.options.sortBy = [sortBy[0]]
      this.tableCollection.options.page = data.page
      await this.loadCollection({
        isCharge: 'false',
        startDate,
        endDate,
        bank: this.bank,
        restaurant: this.restaurant._id,
        limit: data.itemsPerPage,
        skip: (data.itemsPerPage * (data.page - 1)),
        sort: sortDesc[0] ? sortBy[0] : `-${sortBy[0] || 'date'}`
      })
    },
    async loadCollection (params = null) {
      if (this.bank && (!this.hasFinerioAccount || this.account) && this.selectedCalendarDates.length === 2) {
        const [startDate, endDate] = this.selectedCalendarDates
        params = params || {
          isCharge: 'false',
          startDate,
          endDate,
          bank: this.bank,
          restaurant: this.restaurant._id,
          limit: this.tableCollection.options.itemsPerPage,
          skip: (this.tableCollection.options.itemsPerPage * (this.tableCollection.options.page - 1))
        }
        if (this.hasFinerioAccount && this.account) params.accountId = this.account

        this.overlay = true
        this.tableCollection.loading = true
        try {
          const response = await this.$api.get(`/v1/banks/transactions`, params)
          this.tableCollection.data = response.data
          this.tableCollection.total = response.total
        } catch (error) {
          this.collectionCharges = {}
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
        this.tableCollection.loading = false
      }
    },
    async loadBanks (params = null) {
      params = params || {
        restaurant: this.restaurant._id
      }
      this.banks.loading = true
      try {
        const response = await this.$api.get(`/v1/financial/balance-accounts`, params)
        this.banks.data = response
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.banks.loading = false
    },
    async loadBankAccounts (params = null) {
      params = params || {
        credentialId: this.banks.data.find(item => item._id === this.bank).credentialId
      }
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/banks/accounts`, params)
        this.accounts = response
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    async loadPaymentMethods (params = null) {
      params = params || {
        type: 'paymentMethod',
        restaurant: this.restaurant._id
      }
      this.paymentMethods.loading = true
      try {
        const response = await this.$api.get(`/v1/cashflows`, params)
        this.paymentMethods.data = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.paymentMethods.loading = false
    },
    relatePaymentType: async function (paymentMethodId, transactionId) {
      const payload = {
        paymentMethod: paymentMethodId
      }
      this.overlay = true
      try {
        await this.$api.post(`/v1/banks/transactions/${transactionId}/relate-payment-method`, payload)
        const [startDate, endDate] = this.selectedCalendarDates
        this.loadCollection({
          isCharge: 'false',
          startDate,
          endDate,
          bank: this.bank,
          restaurant: this.restaurant._id,
          limit: this.tableCollection.options.itemsPerPage,
          skip: (this.tableCollection.options.itemsPerPage * (this.tableCollection.options.page - 1)),
          sort: this.tableCollection.options.sortDesc[0] ? this.tableCollection.options.sortBy[0] : `-${this.tableCollection.options.sortBy[0] || 'date'}`
        })
        if (!this.collectionApplied(paymentMethodId)) {
          this.openCollectionDetailsPanel()
        }
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
        this.loadCollection()
      }
      this.overlay = false
    },
    async loadCategories (params = null) {
      params = params || {
        type: 'income',
        restaurant: this.restaurant._id
      }
      this.incomeCategories.loading = true
      try {
        const response = await this.$api.get(`/v1/cashflows`, params)
        this.incomeCategories.data = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.incomeCategories.loading = false
    },
    relateIncomeCategory: async function (incomeCategoryId, transactionId) {
      const payload = {
        incomeCategory: incomeCategoryId
      }
      this.overlay = true
      try {
        await this.$api.post(`/v1/banks/transactions/${transactionId}/relate-income-category`, payload)
        const [startDate, endDate] = this.selectedCalendarDates
        this.loadCollection({
          isCharge: 'false',
          startDate,
          endDate,
          bank: this.bank,
          restaurant: this.restaurant._id,
          limit: this.tableCollection.options.itemsPerPage,
          skip: (this.tableCollection.options.itemsPerPage * (this.tableCollection.options.page - 1)),
          sort: this.tableCollection.options.sortDesc[0] ? this.tableCollection.options.sortBy[0] : `-${this.tableCollection.options.sortBy[0] || 'date'}`
        })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
        this.loadCollection()
      }
      this.overlay = false
    },
    relateIncomeType: async function (incomeTypeId, transactionId) {
      const payload = {
        incomeType: incomeTypeId
      }
      this.overlay = true
      try {
        await this.$api.post(`/v1/banks/transactions/${transactionId}/relate-income-type`, payload)
        const [startDate, endDate] = this.selectedCalendarDates
        this.loadCollection({
          isCharge: 'false',
          startDate,
          endDate,
          bank: this.bank,
          restaurant: this.restaurant._id,
          limit: this.tableCollection.options.itemsPerPage,
          skip: (this.tableCollection.options.itemsPerPage * (this.tableCollection.options.page - 1)),
          sort: this.tableCollection.options.sortDesc[0] ? this.tableCollection.options.sortBy[0] : `-${this.tableCollection.options.sortBy[0] || 'date'}`
        })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
        this.loadCollection()
      }
      this.overlay = false
    },
    loadCharges: async function (params = null) {
      if (this.selectedCalendarDates.length === 2) {
        const [startDate, endDate] = this.selectedCalendarDates
        params = params || {
          startDate,
          endDate,
          restaurant: this.restaurant._id
        }
        this.overlay = true
        try {
          const response = await this.$api.get(`/v1/charges`, params)
          this.collectionCharges = response
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    closeCollectionAccountDialog () {
      this.collectionAccountDialogOpen = false
    },
    openCollectionDateDialog () {
      this.collectionDateDialogOpen = true
    },
    closeCollectionDateDialog () {
      this.collectionDateDialogOpen = false
    },
    isEmpty,
    collectionDialogNext () {
      this.collectionAccountDialogOpen = false
      this.collectionDateDialogOpen = true
    },
    collectionDialogBack () {
      this.collectionDateDialogOpen = false
      this.collectionAccountDialogOpen = true
    },
    submitCollectionDialog () {
      this.collectionAccountDialogOpen = false
      this.collectionDateDialogOpen = false
      this.loadCollection()
    },
    confirmCalendarDates () {
      this.$refs.dialog.save(this.selectedCalendarDates)
      this.loadCollection()
    },
    async onChangeBank () {
      this.account = null
      this.accounts.data = []
      this.tableCollection.data = []
      this.collectionCharges = {}

      if (this.hasFinerioAccount) {
        await this.loadBankAccounts()
      } else {
        await this.loadCollection()
      }
    },
    openCollectionDetailsPanel () {
      this.loadCharges()
      this.collectionDetailsPanelOpen = true
    },
    closeCollectionDetailsPanel () {
      this.collectionDetailsPanelOpen = false
    },
    allowedDates (val) {
      const topDate = new Date()
      const evalDate = new Date(moment(val).format('YYYY-MM-DDTHH:mm:ss.SSS'))

      return evalDate.getTime() <= topDate.getTime()
    },
    orderDateRange () {
      this.selectedCalendarDates.sort((a, b) => new Date(a) - new Date(b))
    },
    collectionApplied (paymentMethodId) {
      const category = this.paymentMethods.data.find(item => item._id === paymentMethodId)
      if (category) {
        return !category.needInvoice
      } else {
        return false
      }
    },
    checkIfIncludedInCharges (incomeCategoryId) {
      const found = this.incomeTypes.data.find((item) => item._id === incomeCategoryId)
      return found && found.includedInCharges
    },
    loadIncomeTypes: async function () {
      const params = {
        kind: 'income',
        restaurant: this.$store.state.currentRestaurant.data._id
      }
      this.incomeTypes.loading = true
      try {
        const response = await this.$api.get(`/v1/cashflows/types`, params)
        this.incomeTypes.data = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.incomeTypes.loading = false
    },
    filterCategoriesByType (incomeTypeId) {
      return this.incomeCategories.data.filter(item => !isEmpty(item.concept.type) && item.concept.type._id === incomeTypeId)
    }
  }
}
</script>

<style lang="scss" scoped>
  .sectionTitle {
    font-size: 34px;
    font-weight: bold;
    line-height: 40px;
    color: #646464;
  }

  .paper {
    display: flex;
    padding: 40px 50px 50px 57px;
    border-radius: 26px;
    background-color: #fff;
    flex-direction: column;
    align-items: center;
  }

  .tabs {
    &:hover {
      cursor: pointer;
    }
  }

  .tab {
    font-size: 12px;
  }

  .tabs-items {
    width: 100%;
  }

  .dialogTitle {
    max-width: 21rem;
    display: flex;
    justify-content: center;
    padding-top: 0 !important;
    margin-left: auto;
    margin-right: auto;
    word-break: normal;
    text-align: center;
    font-size: 1.38rem !important;
    color: #01D28E;
  }

  .select {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  .greyButton {
    background-color: #ACABAB !important;
    border-color: #ACABAB !important;
    color: #fff !important;
  }

  .v-pagination__navigation {
    -webkit-box-shadow: none;
    box-shadow: none;
  }

  .v-pagination__item {
    -webkit-box-shadow: none;
    box-shadow: none;
    color: #A4A6AB !important;
  }

  .v-pagination__item--active {
    color: #fff !important;
  }

  .footer {
    font-size: 12px;
    color: #A4A6AB;
  }

  .footerText {
    margin-right: 12px;
    margin-top: auto;
    margin-bottom: auto;
  }

  .footerSelect {
    width: 30px;
    padding-top: 0px;
    margin-top: 0px;
    color: #A4A6AB;
  }
  .footerSelect.v-text-field>.v-input__control>.v-input__slot:before {
    border-style: none;
  }
  .footerSelect.v-text-field>.v-input__control>.v-input__slot:after {
      border-style: none;
  }

  .footerSelect .v-select__selections {
    color: #A4A6AB !important;
  }

  .redText {
    color: #DA4545;
  }

  .blueText {
    color: #0F6FFF;
  }

  .refreshButtonText {
    font-weight: 700;
  }

  .v-application a {
    color: #0F6FFF !important;
  }

  .calendarTextField {
    height: 34px;
    width: 240px;
    border-radius: 22px;

    & ::v-deep .v-label {
      top: auto;
    }

    & ::v-deep fieldset {
      border: 2px solid #01D28E;
    }
  }

  .dateButton {
    text-transform: none;
  }

  .bankDataIcon {
    margin-top: 3.75rem;
    width: 27.94rem;
    height: auto;
  }

  .noDataText {
    margin-top: 1.75rem;
    text-align: center;
    font-weight: 500;
    color: #ACABAB;
  }

</style>

<style lang="scss">
  .tableCollection th {
    font-size: 12px;
  }

  .tableCollection td {
    font-size: 12px;
  }
</style>
